import React, {
  useMemo, useState, useRef
} from 'react'

import {
  Swiper,
  SwiperSlide,
} from 'swiper/react'

import {
  Autoplay,
  EffectFade,
  Pagination,
  Controller,
  A11y,
  Navigation,
  Keyboard,
  Mousewheel
} from 'swiper'

import useIsMobile from 'hooks/useIsMobile'

import CarouselSlide from './CarouselSlide'
import CarouselPreview from './CarouselPreview'

import {
  StyledCarousel,
  StyledCarouselNavigation,
  StyledCarouselNavigationButton,
  StyledCarouselThumbs,
  StyledCarouselThumbsPauseButton
} from './styles'
import Icon from '@/atoms/Icon'

const Carousel = ({ list }) => {
  const isMobile = useIsMobile()
  const liveRegionRef = useRef(null)

  const listThumbs = useMemo(() => {
    const clonedList = [...list]
    const firstEl = clonedList.shift()
    clonedList.push(firstEl)

    clonedList.forEach(el => {
      const regex = /(<([^>]+)>)/ig
      const body = el.title
      const result = body.replace(regex, '')

      el.thumbTitle = result
    })

    return isMobile ? list : clonedList
  }, [list, isMobile])

  const [firstSwiper, setFirstSwiper] = useState(null)
  const [secondSwiper, setSecondSwiper] = useState(null)
  const [isPaused, setIsPaused] = useState(false)

  const updateSlideAccessibility = (swiper) => {
    if (!swiper?.slides) return

    swiper.slides.forEach((slide, index) => {
      const isActive = index === swiper.activeIndex

      slide.setAttribute('aria-hidden', !isActive)
      slide.setAttribute('tabindex', isActive ? '0' : '-1')

      if (!isActive) {
        slide.setAttribute('inert', '')
      } else {
        slide.removeAttribute('inert')
      }

      const focusableElements = slide.querySelectorAll('a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])')
      focusableElements.forEach(element => {
        element.setAttribute('tabindex', isActive ? '0' : '-1')
        if (!isActive) {
          element.setAttribute('aria-hidden', 'true')
        } else {
          element.removeAttribute('aria-hidden')
        }
      })
    })
  }

  const handleSlideChange = (swiper) => {
    updateSlideAccessibility(swiper)

    // Announce slide change for main carousel only
    if (swiper === firstSwiper && list) {
      const currentSlide = swiper.activeIndex + 1
      const totalSlides = swiper.slides.length
      const currentItem = list[swiper.activeIndex]
      setIsPaused(false)

      if (liveRegionRef.current) {
        liveRegionRef.current.textContent = `Slide ${currentSlide} di ${totalSlides}: ${currentItem?.thumbTitle || 'Contenuto'}`
      }
    }
  }

  const handleSwiperInit = (swiper, setSwiper) => {
    setSwiper(swiper)
    setTimeout(() => updateSlideAccessibility(swiper), 0)
  }

  const handlePauseToggle = (e) => {
    if (!firstSwiper) return
    e.stopPropagation()

    if (isPaused) {
      firstSwiper.autoplay.resume()
      setIsPaused(false)
    } else {
      firstSwiper.autoplay.pause()
      setIsPaused(true)
    }
  }

  return (
    <StyledCarousel>
      {/* Live region for screen reader announcements */}
      <div
        ref={liveRegionRef}
        aria-live="polite"
        aria-atomic="true"
        style={{
          position: 'absolute',
          left: '-10000px',
          width: '1px',
          height: '1px',
          overflow: 'hidden'
        }}
      />

      <Swiper
        modules={[EffectFade, Autoplay, Controller, A11y, Navigation, Keyboard, Mousewheel]}
        onSwiper={(swiper) => handleSwiperInit(swiper, setFirstSwiper)}
        onSlideChange={handleSlideChange}
        keyboard={true}
        controller={{ control: secondSwiper }}
        fadeEffect={ { crossFade: true } }
        effect="fade"
        preventInteractionOnTransition
        spaceBetween={0}
        slidesPerView={1}
        watchSlidesProgress
        watchOverflow
        loop={true}
        autoplay={{ disableOnInteraction: false, }}
        role="region"
        aria-label="Slider principale"
        aria-roledescription="Slider"
        onAutoplayTimeLeft={(s, time) => {
          const sliderDuration = s.slides[s.activeIndex].dataset.swiperAutoplay || 8000
          const p = time * 100 / (sliderDuration * 100)
          if (p >= 0 && secondSwiper  && secondSwiper.el) {
            secondSwiper.el.style.setProperty('--slide-progress', 1 - p)
          }
        }}
        breakpoints={{
          768: { allowTouchMove: false }
        }}
        navigation={{
          nextEl: '.slider-button-next',
          prevEl: '.slider-button-prev',
        }}
      >
        {
          list.map((el, i) =>
            <SwiperSlide
              key={`main_${i}`}
              data-swiper-autoplay={el.duration * 1000}
              role="group"
              aria-roledescription="slide"
              aria-label={`${i + 1} di ${list.length}: ${el.thumbTitle || 'Contenuto'}`}
            >
              {({ isActive }) => (
                <CarouselSlide data={el} isActive={isActive} />
              )}
            </SwiperSlide>
          )
        }

        <StyledCarouselNavigation aria-label="Controlli slider principale">
          <StyledCarouselNavigationButton
            className='slider-button-prev'
            aria-label="Vai al contenuto precedente"
            type="button"
          >
            <Icon name='chevron-left' aria-hidden='true' />
          </StyledCarouselNavigationButton>
          <StyledCarouselNavigationButton
            className='slider-button-next'
            aria-label="Vai al contenuto successivo"
            type="button"
          >
            <Icon name='chevron-right' aria-hidden='true' />
          </StyledCarouselNavigationButton>
        </StyledCarouselNavigation>
      </Swiper>

      <StyledCarouselThumbs>
        <Swiper
          watchSlidesProgress
          modules={[EffectFade, Pagination, Controller, A11y]}
          pagination={{ clickable: false }}
          allowTouchMove={false}
          onSwiper={(instance) => {
            if (instance.el) {
              instance.el.style.setProperty('--slide-progress', 0)
            }

            handleSwiperInit(instance, setSecondSwiper)

            // Hide pagination bullets from assistive technology since they're not interactive
            setTimeout(() => {
              if (instance.el) {
                const paginationBullets = instance.el.querySelectorAll('.swiper-pagination-bullet')

                paginationBullets.forEach(bullet => {
                  bullet.setAttribute('aria-hidden', 'true')
                  bullet.setAttribute('tabindex', '-1')
                })
              }
            }, 0)
          }}
          onSlideChange={handleSlideChange}
          controller={{ control: firstSwiper }}
          fadeEffect={ { crossFade: true } }
          effect="fade"
          spaceBetween={0}
          slidesPerView={1}
          loop={true}
          role="region"
          aria-label="Anteprime contenuti dello slider principale"
          aria-roledescription="slider"
        >
          {
            listThumbs.map((el, i) =>
              <SwiperSlide
                key={`preview_${i}`}
                role="group"
                aria-roledescription="slide"
                aria-label={`${i + 1} di ${listThumbs.length}: Anteprima ${el.thumbTitle || 'contenuto'}`}
              >
                <CarouselPreview data={el} />
              </SwiperSlide>
            )
          }

          <StyledCarouselThumbsPauseButton
            onClick={handlePauseToggle}
            aria-label={isPaused ? 'Riprendi slideshow' : 'Pausa slideshow'}
            type="button"
          >
            <Icon name={isPaused ? 'play' : 'pause'} aria-hidden='true' />
          </StyledCarouselThumbsPauseButton>
        </Swiper>
      </StyledCarouselThumbs>
    </StyledCarousel>
  )
}

export default Carousel
